import React, { useState } from "react";
import { Mail, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserSync } from "../hooks/useUserSync.js";

const DISMISS_KEY = "emailReachabilityBannerDismissedAt";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function wasRecentlyDismissed(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_TTL_MS;
}

/** Non-blocking hint for users whose account email (e.g. Apple private relay) can't receive notifications. */
export default function EmailReachabilityBanner() {
  const { user } = useUserSync();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed);

  const needsContactEmail =
    user && user.emailReachable === false && !user.contactEmailVerifiedAt;

  if (!needsContactEmail || dismissed) return null;

  return (
    <div className="bg-golden/20 border-b border-golden/40 px-4 py-2 text-sm text-my-black">
      <div className="container mx-auto flex items-center gap-3">
        <Mail className="w-4 h-4 flex-shrink-0 text-main-purple" />
        <p className="flex-1">
          {t("emailReachability.message")}{" "}
          <button
            type="button"
            onClick={() => navigate("/definicoes")}
            className="font-semibold underline hover:text-main-purple"
          >
            {t("emailReachability.addContactEmail")}
          </button>
        </p>
        <button
          type="button"
          aria-label={t("emailReachability.dismiss")}
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
            setDismissed(true);
          }}
          className="flex-shrink-0 text-gray-500 hover:text-my-black"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
